import type { DeepPartial } from 'ts-essentials';

import type { BookmarkEditKeys, SuggestedBookmarkEdit } from './suggested-bookmark-edits';
import type { SuggestedTableCellEdit, TableCellEditKeys } from './suggested-table-cell-edits';
import type { SuggestedTableRowEdit, TableRowEditKeys } from './suggested-table-row-edits';
import type {
  AlineElement,
  AlineText,
  BreakEditKeys,
  CheckboxEditKeys,
  DocEditKeys,
  DrawingEditKeys,
  FootersEditKeys,
  HeadersEditKeys,
  InstructionFieldEditKeys,
  LinkEditKeys,
  ListEditKeys,
  ParagraphEditKeys,
  ParagraphGlyphEditKeys,
  SuggestedBreakEdit,
  SuggestedCheckboxEdit,
  SuggestedDocEdit,
  SuggestedDrawingEdit,
  SuggestedFootersEdit,
  SuggestedHeadersEdit,
  SuggestedInstructionFieldEdit,
  SuggestedLinkEdit,
  SuggestedListEdit,
  SuggestedParagraphEdit,
  SuggestedParagraphGlyphEdit,
  SuggestedTabEdit,
  SuggestedTableEdit,
  SuggestedTextEdit,
  TabEditKeys,
  TableEditKeys,
  TextEditKeys,
} from '../../internal';

export type EditKeys =
  | BookmarkEditKeys
  | BreakEditKeys
  | DocEditKeys
  | DrawingEditKeys
  | HeadersEditKeys
  | FootersEditKeys
  | InstructionFieldEditKeys
  | LinkEditKeys
  // @deprecated
  | ListEditKeys
  | ParagraphEditKeys
  | ParagraphGlyphEditKeys
  | TabEditKeys
  | TableEditKeys
  | TableRowEditKeys
  | TableCellEditKeys
  | TextEditKeys
  | CheckboxEditKeys;

/**
 * TODO: This should be a Based type which means each of the suggested edits
 * should extend this type. Then there should an `Edit` or `SuggestedEdit` type
 * that combines them together as a discriminated union.
 *
 * TODO: The `type` below should NOT be `EditKeys` it should be `string`.
 * Then `EditKeys` should be derived from `SuggestedEdit` using `type
 * SuggestedEditKey = SuggestedEdit['type']`.
 */
export type SuggestedEditValue =
  | DeepPartial<Omit<AlineElement, 'children' | 'suggestedEdits' | 'type'>>
  | DeepPartial<Omit<AlineText, 'comments' | 'suggestedEdits' | 'text'>>;

export interface BaseEdit {
  authorId: string; // Can be a UUID of a user in our system or just a string of a name (e.g, from DOCX import).
  type: EditKeys;
  value: SuggestedEditValue;
  createdAt?: string;
  /** Links related edits into one logical suggestion */
  groupId?: string;
  /**
   * Why the suggestion was made — the user-facing explanation the suggestions
   * toolbar shows. Stored once per suggestion, on the first sourced edit (in
   * document order) of each contiguous run of blocks; readers take the first
   * reasoning they find among the group's edits. Payload, not identity: edit
   * merge/dedupe logic must never let a reasoning-less duplicate replace an
   * edit that carries reasoning.
   */
  reasoning?: string;
  updatedAt?: string;
}

export type SuggestedEdit =
  | SuggestedBookmarkEdit
  | SuggestedBreakEdit
  | SuggestedCheckboxEdit
  | SuggestedDocEdit
  | SuggestedDrawingEdit
  | SuggestedHeadersEdit
  | SuggestedFootersEdit
  | SuggestedInstructionFieldEdit
  | SuggestedLinkEdit
  // @deprecated
  | SuggestedListEdit
  | SuggestedParagraphEdit
  | SuggestedParagraphGlyphEdit
  | SuggestedTabEdit
  | SuggestedTableEdit
  | SuggestedTableCellEdit
  | SuggestedTableRowEdit
  | SuggestedTextEdit;

/**
 * A categorization of each edit type into one of three categories, for the purpose of creating more
 * general types and type guards for each category.
 */
export const editCategories = {
  deleteNode: 'remove',
  deleteParagraph: 'remove',
  footerFormat: 'format',
  headerFormat: 'format',
  insertBookmark: 'insert',
  insertBreak: 'insert',
  insertCheckbox: 'insert',
  insertDrawing: 'insert',
  insertInstructionField: 'insert',
  /** Wraps nodes in a link element. */
  insertLink: 'format',
  insertNode: 'insert',
  insertParagraph: 'insert',
  insertTab: 'insert',
  insertTableCell: 'insert',
  insertTableRow: 'insert',
  linkEdit: 'format',
  listFormat: 'format',
  pageDimensions: 'format',
  pageFormat: 'format',
  paragraphFormat: 'format',
  removeBreak: 'remove',
  removeCheckbox: 'remove',
  removeDrawing: 'remove',
  removeInstructionField: 'remove',
  /** Unwraps a link element. */
  removeLink: 'format',
  removeTab: 'remove',
  removeTableCell: 'remove',
  removeTableRow: 'remove',
  styleSheet: 'format',
  tableCellFormat: 'format',
  tableFormat: 'format',
  textFormat: 'format',
  toggleCheckbox: 'format',
  variant: 'format',
} satisfies Record<SuggestedEdit['type'], 'insert' | 'remove' | 'format'>;

type EditByCategory<C extends 'insert' | 'remove' | 'format'> = {
  [T in SuggestedEdit['type']]: (typeof editCategories)[T] extends C
    ? Extract<SuggestedEdit, { type: T }>
    : never;
}[SuggestedEdit['type']];

export type AnyInsertEdit = EditByCategory<'insert'>;
export type AnyRemoveEdit = EditByCategory<'remove'>;
export type AnyFormatEdit = EditByCategory<'format'>;
